import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import HomeHero from './components/home/HomeHero';
import AboutOutpost from './components/home/AboutOutpost';
import TeamBand from './components/home/TeamBand';
import ServicesShowcase from './components/home/ServicesShowcase';
import InstagramFeed from './components/home/InstagramFeed';
import VanBand from './components/home/VanBand';
import CategoryGrid from './pages/CategoryGrid';
import AdminCategories from './pages/AdminCategories';
import ProductListing from './pages/ProductListing';
import ProductDetailsNew from './pages/ProductDetailsNew';
import NewShopFrontPage from './pages/NewShopFrontPage';
import Collections from './pages/Collections';
import AdminLogin from './pages/admin/AdminLogin';
import AdminShell from './components/admin/shell/AdminShell';
import Dashboard from './pages/admin/Dashboard';
import Analytics from './pages/admin/Analytics';
import AdminUsers from './pages/admin/AdminUsers';
import AdminBlogEditor from './pages/admin/AdminBlogEditor';
import Catalogue from './pages/admin/Catalogue';
import AdminLiveChat from './pages/admin/AdminLiveChat';
import Collection from './pages/Collection';
import AllSignage from './pages/AllSignage';
import PavementSigns from './pages/PavementSigns';
import ProjectingSigns from './pages/ProjectingSigns';
import VehicleSignwriting from './pages/VehicleSignwriting';
import GlassManifestation from './pages/GlassManifestation';
import WindowPrivacyFilm from './pages/WindowPrivacyFilm';
import Signboards from './pages/Signboards';
import Gazebos from './pages/Gazebos';
import Parasols from './pages/Parasols';
import Tablecloths from './pages/Tablecloths';
import AllDisplay from './pages/AllDisplay';
import PvcBanners from './pages/PvcBanners';
import RaisedLettering from './pages/RaisedLettering';
import WindowGraphics from './pages/WindowGraphics';
import PullUpBanners from './pages/PullUpBanners';
import HerasFencingBanners from './pages/HerasFencingBanners';
import EventBoards from './pages/EventBoards';
import TabletopPos from './pages/TabletopPos';
import CarpetMats from './pages/CarpetMats';
import PopUpDisplays from './pages/PopUpDisplays';
import FeatherFlags from './pages/FeatherFlags';
import CafeBarriers from './pages/CafeBarriers';
import MagneticPanels from './pages/MagneticPanels';
import LoaderDemo from './pages/LoaderDemo';
import PrintingLandingPage from './pages/PrintingLandingPage';
import PrintingCategoryPage from './pages/PrintingCategoryPage';
import PrintingProductPage from './pages/PrintingProductPage';
import PrintingBrowserPage from './pages/PrintingBrowserPage';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import CaseStudyPage from './pages/CaseStudyPage';
import ClothingBrowsePage from './pages/ClothingBrowsePage';
import QuotePage from './pages/QuotePage';
import { CartProvider } from './contexts/CartContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { ContactModalProvider } from './contexts/ContactModalContext';
import UnifiedChatWidget from './components/UnifiedChatWidget';
import PageLoader from './components/PageLoader';
import ScrollToTop from './components/ScrollToTop';
import PostHogTracker from './components/PostHogTracker';

// Lazy so the gsap plugins and ogl stay out of the main bundle
const AnimationOptions = React.lazy(() => import('./pages/AnimationOptions'));
const PrintingBrowserAPage = React.lazy(() => import('./pages/PrintingBrowserAPage'));
const PrintingBrowserBPage = React.lazy(() => import('./pages/PrintingBrowserBPage'));
const PrivacyPolicyPage =React.lazy(() => import('./pages/PrivacyPolicy'));

const PrivacyPolicy = () => (
  <React.Suspense fallback={<div className="min-h-screen bg-white" />}>
    <PrivacyPolicyPage />
  </React.Suspense>
);

// Print catalogue mockups A and B (unlinked, noindex)
const PrintMockupA = () => (
  <React.Suspense fallback={<div className="min-h-screen bg-white" />}>
    <PrintingBrowserAPage />
  </React.Suspense>
);
const PrintMockupB = () => (
  <React.Suspense fallback={<div className="min-h-screen bg-[#0b0b0b]" />}>
    <PrintingBrowserBPage />
  </React.Suspense>
);

// The v2 preview became mockup A; old links keep their product and query.
function PrintV2Redirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/printing/all-a${slug ? `/${slug}` : ''}${window.location.search}`} replace />;
}

function HomePage() {
  return (
    <>
      <HomeHero />
      <AboutOutpost />
      <TeamBand />
      <ServicesShowcase />
      <InstagramFeed />
      <VanBand />
    </>
  );
}

// Wrapper to force remount when styleCode changes
function ProductDetailsNewWrapper() {
  const { styleCode } = useParams<{ styleCode: string }>();
  return <ProductDetailsNew key={styleCode} />;
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <PostHogTracker />
      <AuthProvider>
        <ThemeProvider>
          <CartProvider>
            <ContactModalProvider>
            <WishlistProvider>
            <Routes>
              {/* Admin Routes - No Header/Footer */}
              <Route path="/admin/login" element={<AdminLogin />} />
              {/* Admin: one shell, plan in docs/plans/2026-09-26-admin-overhaul-plan.md.
                  Users and categories are out of the nav; the avatar menu reaches them. */}
              <Route path="/admin" element={<AdminShell />}>
                <Route index element={<Dashboard />} />
                <Route path="dashboard" element={<Navigate to="/admin" replace />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="catalogue" element={<Catalogue />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="blog" element={<AdminBlogEditor />} />
                <Route path="livechat" element={<AdminLiveChat />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Route>

            {/* Public Routes - With Header/Footer */}
            <Route path="*" element={
              <div className="App">
                <Header />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/shop" element={<NewShopFrontPage />} />
                  <Route path="/categories" element={<CategoryGrid />} />
                  <Route path="/categories/:categoryId" element={<ProductListing />} />
                  <Route path="/collections" element={<Collections />} />
                  <Route path="/collections/:type" element={<Collection />} />
                  <Route path="/products/:styleCode" element={<ProductDetailsNewWrapper />} />
                  <Route path="/services/all-signage" element={<AllSignage />} />
                  <Route path="/services/pavement-signs" element={<PavementSigns />} />
                  <Route path="/services/projecting-signs" element={<ProjectingSigns />} />
                  <Route path="/services/glass-manifestation" element={<GlassManifestation />} />
                  <Route path="/services/window-privacy-film" element={<WindowPrivacyFilm />} />
                  <Route path="/services/signboards" element={<Signboards />} />
                  <Route path="/services/gazebos" element={<Gazebos />} />
                  <Route path="/services/parasols" element={<Parasols />} />
                  <Route path="/services/tablecloths" element={<Tablecloths />} />
                  <Route path="/services/vehicle-signwriting" element={<VehicleSignwriting />} />
                  <Route path="/services/magnetic-panels" element={<MagneticPanels />} />
                  <Route path="/services/pvc-banners" element={<PvcBanners />} />
                  <Route path="/services/raised-lettering" element={<RaisedLettering />} />
                  <Route path="/services/window-graphics" element={<WindowGraphics />} />
                  <Route path="/services/pull-up-banners" element={<PullUpBanners />} />
                  <Route path="/services/heras-fencing-banners" element={<HerasFencingBanners />} />
                  <Route path="/services/event-boards" element={<EventBoards />} />
                  {/* Display */}
                  <Route path="/services/all-display" element={<AllDisplay />} />
                  <Route path="/services/tabletop-pos" element={<TabletopPos />} />
                  <Route path="/services/carpet-mats" element={<CarpetMats />} />
                  <Route path="/services/pop-up-displays" element={<PopUpDisplays />} />
                  <Route path="/services/feather-flags" element={<FeatherFlags />} />
                  <Route path="/services/cafe-barriers" element={<CafeBarriers />} />
                  <Route path="/loader-demo" element={<LoaderDemo />} />
                  <Route
                    path="/animation-options"
                    element={
                      <React.Suspense fallback={<div className="min-h-screen bg-black" />}>
                        <AnimationOptions />
                      </React.Suspense>
                    }
                  />
                  {/* Printing Routes */}
                  <Route path="/printing" element={<PrintingLandingPage />} />
                  <Route path="/printing/all" element={<PrintingBrowserPage />} />
                  <Route path="/printing/all/:slug" element={<PrintingBrowserPage />} />
                  <Route path="/printing/all-a" element={<PrintMockupA />} />
                  <Route path="/printing/all-a/:slug" element={<PrintMockupA />} />
                  <Route path="/printing/all-b" element={<PrintMockupB />} />
                  <Route path="/printing/all-b/:slug" element={<PrintMockupB />} />
                  <Route path="/printing/all-v2" element={<PrintV2Redirect />} />
                  <Route path="/printing/all-v2/:slug" element={<PrintV2Redirect />} />
                  <Route path="/printing/:category" element={<PrintingCategoryPage />} />
                  <Route path="/printing/:category/:slug" element={<PrintingProductPage />} />
                  {/* Clothing Routes */}
                  <Route path="/clothing" element={<ClothingBrowsePage />} />
                  <Route path="/clothing/all" element={<ClothingBrowsePage />} />
                  {/* Quote flow — full screens, one URL per step */}
                  <Route path="/quote" element={<QuotePage />} />
                  <Route path="/quote/:step" element={<QuotePage />} />
                  {/* Blog Routes */}
                  <Route path="/blog" element={<BlogList />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/case-study/:slug" element={<CaseStudyPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                </Routes>
                <Footer />
                  <UnifiedChatWidget />
                </div>
              } />
            </Routes>
            </WishlistProvider>
            </ContactModalProvider>
          </CartProvider>
        </ThemeProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
